/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { forwardRef, useEffect, useState } from 'react';
import { createContext } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import Split from 'react-split';
import { IoProvider, useSocket } from 'socket.io-react-hook';

import { useRunViewerVideoPlayer } from './useRunViewerVideo';

import { RunViewerData, StartExecutionResponse } from '@/commons/commons';
import { Step } from '@/commons/data';
import { RunStatus } from '@/commons/protocols';
import { Button } from '@/components/atoms/Button';
import { formatError } from '@/components/atoms/Error';
import { StatusIcon } from '@/components/atoms/StatusIcon';
import TruncatedTextWithPopover from '@/components/atoms/TruncatedTextWithPopover';
import ActionBugs from '@/components/runViewer/ActionBugs';
import ReportDialog from '@/components/runViewer/ReportDialog';
import RunViewerSteps from '@/components/runViewer/RunViewerSteps';
import TraceLink from '@/components/runViewer/TraceLink';
import SuiteActions from '@/components/suites/SuiteActions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { stripProtocol } from '@/lib/utils';
import { useGet, useMutate } from '@/net/commons';
import { useUserContext, useWindowDimensions } from '@/pages/Root';
import { cn, displayUser, parseIndex } from '@/utils';

type StepContextType = {
    runStatus: RunStatus;
    highlightedStepRank: number | undefined;
    highlightedAction: number | undefined;
    handleClickOnStepOrAction: (stepRank: number, actionRank?: number) => void;
};

export const StepContext = createContext<StepContextType>({
    runStatus: 'queued',
    highlightedStepRank: undefined,
    highlightedAction: undefined,
    handleClickOnStepOrAction: () => {},
});

const RunViewer = forwardRef(() => {
    const { width } = useWindowDimensions();
    const { projectSlug, suiteSlug, executionId, runId } = useParams();
    const [status, setStatus] = useState<undefined | RunStatus | 'running-testagent'>(undefined);
    const [attempt, setAttempt] = useState<number>(1);
    const [stepsFromSocket, setStepsFromSocket] = useState<Step[] | undefined>(undefined);
    const [currentUrl, setCurrentUrl] = useState<string | undefined>(undefined);
    const [sessionStatus, setSessionStatus] = useState('');
    const [debugPageId, setDebugPageId] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const { isAdmin, isImpersonated } = useUserContext().user;
    const hasAdminPermissions = isAdmin || isImpersonated;
    const [highlightedStepRank, setHighlightedStepRank] = useState<number | undefined>(
        parseIndex(searchParams.get('step')),
    );
    const [highlightedAction, setHighlightedAction] = useState<number | undefined>(
        parseIndex(searchParams.get('action')),
    );
    const attemptParam = ((a) => {
        if (a === null) return undefined;
        const p = parseInt(a);
        if (isNaN(p)) return undefined;
        return p;
    })(searchParams.get('attempt'));

    const { data, isLoading, mutate } = useGet<RunViewerData>(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}${
            attemptParam ? `?attempt=${attemptParam}` : ''
        }`,
        {
            refreshInterval: () =>
                status === 'queued' || status === undefined || status === 'running-testagent' ? 500 : 0,
        },
    );

    const { videoComponent, setPlayHeadToAction } = useRunViewerVideoPlayer({
        runData: data,
        projectSlug,
        suiteSlug,
        executionId,
        runId,
        updateActionCallback: setHighlightedAction,
        updateStepRankCallback: setHighlightedStepRank,
    });

    const wsProtocol = process.env.NODE_ENV !== 'development' ? 'wss' : 'ws';
    const wsUrl =
        process.env.NODE_ENV !== 'development' || import.meta.env.VITE_CLUSTER_SETUP === 'true'
            ? `${stripProtocol(import.meta.env.VITE_CLUSTER_URL)}/${data?.run?.sessionUuid}/debug/devtools/page/${debugPageId}`
            : `localhost:9222/devtools/page/${debugPageId}`;

    const { socket } = useSocket(import.meta.env.VITE_CLUSTER_URL, {
        enabled: status === 'running' || status === 'running-testagent',
        reconnection: true,
        reconnectionAttempts: 5,
        path:
            process.env.NODE_ENV !== 'development' || import.meta.env.VITE_CLUSTER_SETUP === 'true'
                ? `/${data?.run?.sessionUuid}/node/agent`
                : '/agent',
        extraHeaders: {
            'x-heal-kind': 'studio',
        },
    });

    // Handle the user clicking on an action card
    const handleClickOnStepOrAction = (stepRank: number, actionRank?: number) => {
        setHighlightedStepRank(stepRank);
        setSearchParams((prev) => {
            prev.set('step', stepRank.toString());
            return prev;
        });
        actionRank !== undefined &&
            setHighlightedAction(actionRank) &&
            setSearchParams((prev) => {
                prev.set('action', actionRank.toString());
                return prev;
            });
        const action = data?.runAttempt?.steps[stepRank].actions[actionRank ?? 0];

        action && setPlayHeadToAction(action);
    };

    useEffect(() => {
        if (data?.run.status !== status) {
            setStatus(data?.run.status);
        }
        if (data?.run.status === 'finished' || data?.run.status === 'failed') {
            setAttempt(attemptParam !== undefined ? attemptParam : data.runAttempt?.attempt || 1);
            setCurrentUrl(data.story.entryHref);
        }
    }, [data, isLoading]);

    useEffect(() => {
        if (socket) {
            socket.on('connect', () => {
                socket.emit('getHistory');
            });

            socket.on('status', ({ status: st, debugPageId: id }) => {
                setSessionStatus(st);
                setDebugPageId(id);
            });

            socket.on('webAgentStarted', ({ attempt: a }) => {
                setStatus('running');
                setStepsFromSocket([]);
                setAttempt(a);
            });

            socket.on('eventsHistory', (eventsHistory) => {
                setStepsFromSocket((previousHistory) => {
                    // If there's already an history this is an noisy message, ignore
                    if (previousHistory !== undefined) {
                        return previousHistory;
                    }

                    // For each step, we only keep the latest stepUpdate
                    const stepUpdatesHistory = eventsHistory
                        .filter((e: any) => e[0] === 'stepUpdate')
                        .map((e: [string, { step: Step }]) => e[1].step);
                    const ranks = stepUpdatesHistory.map((s: Step | undefined) => s?.rank);
                    const res = [];
                    for (const rank of ranks) {
                        res.push(stepUpdatesHistory.filter((s: Step | undefined) => s?.rank === rank).pop());
                    }
                    return res;
                });

                // Update currentURL to latest in history
                const urlHistory = eventsHistory?.filter((e: any) => e?.length > 0 && e[0] === 'navigatedTo');
                if (urlHistory.length > 0) {
                    urlHistory[urlHistory.length - 1][1]?.url &&
                        setCurrentUrl(urlHistory[urlHistory.length - 1][1].url);
                }

                // Determine if the session started with a new attempt
                const maybeStarted = eventsHistory?.filter((e: any) => e[0] === 'webAgentStarted');
                if (maybeStarted.length > 0) {
                    const a = maybeStarted.pop()[1]?.attempt;
                    setAttempt(a || 1);
                    setStatus('running');
                }

                // Determine if web agent session has ended and the attempt
                const maybeEndedEvents = eventsHistory?.filter((e: any) => e[0] === 'webAgentEnded');
                if (maybeEndedEvents.length > 0) {
                    const a = maybeEndedEvents.pop()[1]?.attempt;
                    setAttempt(a || 1);
                    setStatus('running-testagent');
                }

                const maybeSessionStatus = eventsHistory?.filter((e: any) => e[0] === 'status');
                if (maybeSessionStatus.length > 0) {
                    const { status: st, debugPageId: id } = maybeSessionStatus.pop()[1];
                    setSessionStatus(st);
                    setDebugPageId(id);
                }
            });

            socket.on('stepUpdate', ({ step }: { step: Step }) => {
                if (status === 'running' || status === 'queued') {
                    setStepsFromSocket((previousSteps) => {
                        if (previousSteps === undefined || previousSteps.length === 0) return [step];
                        // if step already has a reported result, update it
                        if (previousSteps.map((s) => s?.rank).includes(step?.rank)) {
                            return previousSteps.map((s) => (s?.rank === step?.rank ? step : s));
                        }
                        // Otherwise add the step to
                        return [...previousSteps, step];
                    });
                }
            });

            socket.on('navigatedTo', ({ url }: { url: string }) => {
                setCurrentUrl(url);
            });

            socket.on('webAgentEnded', ({ attempt: a }) => {
                setStatus('running-testagent');
                setAttempt(a);
            });
        }

        return () => {
            if (socket) {
                socket.off('connect');
                socket.off('disconnect');
                socket.off('status');
                socket.off('webAgentStarted');
                socket.off('eventsHistory');
                socket.off('stepUpdate');
                socket.off('navigatedTo');
                socket.off('webAgentEnded');
            }
        };
    }, [socket, status]);

    const navigate = useNavigate();

    const isFinished =
        data?.run.status !== 'running' && data?.run.status !== 'queued' && data?.run.status !== 'canceled';

    const { trigger: triggerRun } = useMutate<StartExecutionResponse>(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/trigger`,
        {
            revalidate: true,
            method: 'POST',
        },
    );

    const handleRun = async (storyId: number) => {
        try {
            const res = await triggerRun({ storyIds: [storyId] });
            navigate(`/run/${projectSlug}/${suiteSlug}/${res?.execution.id}/${res?.runMessages[0]?.runId}`);
        } catch (err) {
            toast({
                title: `Error starting run`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const { trigger: triggerEditMeta } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id`, {
        revalidate: true,
        method: 'PATCH',
    });

    const [debugMode, setDebugMode] = useState(false);
    const toggleDebugMode = () => setDebugMode(!debugMode);

    const [hidePlaywright, setHidePlaywright] = useState(false);
    const toggleHidePlaywright = () => setHidePlaywright(!hidePlaywright);

    const handleToggleEnable = async (storyId: number, enabled: boolean) => {
        try {
            await triggerEditMeta({ idParam: storyId, enabled });
            mutate();
            toast({
                title: enabled ? 'Turned story on' : 'Turned story off',
            });
        } catch (err) {
            toast({
                title: `Error ${enabled ? 'disabling' : 'enabling'} story`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    //TODO If there was a FAIL, that step is selected by default when opening the page

    if (isLoading) {
        return <div>Loading...</div>;
    } else if (!data) {
        return <div>No data</div>;
    }

    const renderAgentVersion = (agentVersion: string | null | undefined) => {
        const agentCommitText = agentVersion !== 'dev' && agentVersion?.substring(0, 7);
        const agentCommitHref = `https://github.com/heal-dev/browserRunner/commit/${agentVersion}`;
        return agentCommitText ? <a href={agentCommitHref}>{agentCommitText}</a> : agentVersion;
    };

    return (
        <div className="flex flex-col h-full overflow-hidden rounded-[10px] bg-white mx-[6px] drop-shadow-[0_0_10px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col mb-1 h-[7%] p-2" key="title">
                <div className="flex flex-row justify-between">
                    <div className="flex flex-row items-center gap-1 self-start">
                        <h1 className="text-xl ">
                            <Link
                                to={`/story/${projectSlug}/${suiteSlug}/${data.story.slug}/${data.run.storyId}`}
                                className="hover:bg-gray-50"
                            >
                                <TruncatedTextWithPopover
                                    text={data.story.name}
                                    maxLength={width > 500 ? (width > 800 ? (width > 1250 ? 120 : 70) : 45) : 20}
                                />
                            </Link>
                        </h1>
                        {status === 'running' ? (
                            <div
                                className="gap-1 flex items-center
                                     rounded-sm bg-gray-100 px-2 h-min py-1 text-xs align-baseline"
                            >
                                <StatusIcon status={data.run.status} result={data.run.result} spinnerSize={2.5} />
                                running agent
                            </div>
                        ) : status === 'running-testagent' ? (
                            <div
                                className="gap-1 flex items-center
                                     rounded-sm bg-gray-100 px-2 h-min py-1 text-xs align-baseline"
                            >
                                <StatusIcon status={'running'} result={data?.result?.result} spinnerSize={2.5} />
                                analyzing results
                            </div>
                        ) : (
                            <StatusIcon
                                status={data.run.status}
                                result={data?.result?.result}
                                output={data?.result?.output}
                                maintenance={data?.story?.maintenance}
                            />
                        )}
                        {data.attemptIndexes.length > 1 && (
                            <div
                                className="gap-1 flex items-center
                                 rounded-sm bg-gray-100 px-2 h-min py-1 text-xs align-baseline"
                            >
                                <Select
                                    value={attempt.toString()}
                                    onValueChange={(value) => {
                                        const newAttempt = parseInt(value);
                                        setAttempt(newAttempt);
                                        setSearchParams((prev) => {
                                            prev.set('attempt', newAttempt.toString());
                                            return prev;
                                        });
                                    }}
                                >
                                    <SelectTrigger className="bg-transparent border-none outline-none text-xs h-auto p-0">
                                        <SelectValue placeholder={`Attempt ${attempt}`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {data.attemptIndexes.map((a) => (
                                            <SelectItem key={a} value={a.toString()}>
                                                Attempt {a}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                        {hasAdminPermissions && (
                            <>
                                <button className="shrink-0" onClick={toggleHidePlaywright}>
                                    <img
                                        className={cn(
                                            'w-8 transition-[filter] duration-300',
                                            hidePlaywright && 'force-greyOut',
                                        )}
                                        src="/playwright_logo.svg"
                                        alt="Toggle Playwright"
                                    />
                                    <span className="sr-only">{`${hidePlaywright ? 'Show' : 'Hide'} Playwright selectors`}</span>
                                </button>
                                <Button onClick={toggleDebugMode} theme="default" color="purple" size="small">
                                    {debugMode ? 'Hide Debug' : 'Show Debug'}
                                </Button>
                                {(data?.run?.status === 'finished' || data?.run?.status === 'failed') && (
                                    <ReportDialog
                                        currentOutput={data.result?.output}
                                        currentReport={data.result?.report}
                                        projectSlug={projectSlug}
                                        suiteSlug={suiteSlug}
                                        executionId={executionId}
                                        runId={runId}
                                    />
                                )}
                            </>
                        )}
                        {projectSlug && suiteSlug && data?.story.archived === false && (
                            <SuiteActions
                                projectSlug={projectSlug}
                                suiteSlug={suiteSlug}
                                storyName={data.story.name}
                                storySlug={data.story.slug}
                                storyId={data.run.storyId}
                                submiting={false}
                                archived={data.story.archived}
                                maintenance={data.story.maintenance}
                                handleRun={() => handleRun(data.run.storyId)}
                                handleToggleEnable={() => {
                                    if (data.run.storyId)
                                        handleToggleEnable(
                                            data.run.storyId,
                                            data.story.enabled !== undefined ? !data.story.enabled : false,
                                        );
                                }}
                                draft={data.story.draft}
                                enabled={data.story.enabled}
                                executionId={executionId}
                                runId={runId}
                            />
                        )}
                    </div>
                </div>
                <div className="flex flex-row justify-between">
                    <div className="text-xs text-muted-foreground">
                        {data?.run?.status !== 'queued' ? (
                            <>
                                {data?.triggerUser ? `Started ${displayUser(data.triggerUser)} with a` : 'A'}
                                gent version "{renderAgentVersion(data.run.agentCommitHash)}"{' '}
                            </>
                        ) : (
                            'Waiting for agent to start '
                        )}
                        on{' '}
                        <Link
                            className="hover:bg-gray-50"
                            to={data?.run.entryHref ? data.run.entryHref : data.story.entryHref}
                        >
                            <TruncatedTextWithPopover
                                text={data?.run.entryHref ? data.run.entryHref : data.story.entryHref}
                                maxLength={width > 600 ? (width > 900 ? (width > 1250 ? 120 : 70) : 45) : 10}
                            />
                        </Link>
                    </div>
                    {projectSlug && suiteSlug && executionId && runId && (
                        <div className="flex flex-row items-center">
                            <TraceLink
                                projectSlug={projectSlug}
                                suiteSlug={suiteSlug}
                                executionId={executionId}
                                runId={runId}
                                isFinished={isFinished}
                                attempt={attemptParam !== undefined ? attemptParam : data.runAttempt?.attempt || 1}
                            />
                        </div>
                    )}
                </div>
            </div>
            <Split className="split border-t min-h-[93%]" gutterSize={4} key="viewer">
                <div id="left-pane" className="flex flex-col max-h-max w-full" aria-label="steps">
                    <Split
                        className="flex flex-col h-full overflow-hidden"
                        sizes={[67, 33]}
                        gutterSize={4}
                        key="right-pane"
                        gutterStyle={(_dimension, gutterSize) => {
                            return { height: `${gutterSize}px` };
                        }}
                        direction="vertical"
                    >
                        <div
                            id="top-left-pane"
                            className="flex flex-col h-2/3 w-full overflow-y-auto pt-1 @container/steps
                        px-2 md:px-4 md:mt-2
                        overflow-x-clip text-sm"
                            key="steps-panel"
                        >
                            {isLoading ? (
                                <div className="p-2 text-sm">Loading...</div>
                            ) : (
                                <StepContext.Provider
                                    value={{
                                        runStatus: data.run.status,
                                        highlightedStepRank,
                                        highlightedAction,
                                        handleClickOnStepOrAction,
                                    }}
                                >
                                    <RunViewerSteps
                                        stepsToRun={data.story.steps}
                                        stepResultsFromBack={data.runAttempt?.steps ?? []}
                                        stepResultsFromWebSocket={stepsFromSocket ?? []}
                                        storyConfig={data.story}
                                        blocksConfig={data.blocks}
                                        debugMode={debugMode}
                                        hidePlaywright={hidePlaywright}
                                    />
                                </StepContext.Provider>
                            )}
                        </div>

                        <div id="bottom-left-pane" className="flex flex-col w-full h-1/3 p-2" key="rca-panel">
                            {highlightedStepRank !== undefined ? (
                                <StepContext.Provider
                                    value={{
                                        runStatus: data.run.status,
                                        highlightedStepRank,
                                        highlightedAction,
                                        handleClickOnStepOrAction,
                                    }}
                                >
                                    <ActionBugs
                                        stepResultsFromBack={data.runAttempt?.steps ?? []}
                                        stepResultsFromWebSocket={[]}
                                        error={data.result?.error}
                                    />
                                </StepContext.Provider>
                            ) : (
                                data?.run?.status === 'finished' && (
                                    <span className="text-sm">Select a step to view details</span>
                                )
                            )}
                        </div>
                    </Split>
                </div>
                <div id="right-pane" className="flex flex-col h-full" aria-label="recording">
                    <div
                        id="right-pane-top"
                        className="flex flex-col h-full gap-2 rounded [perspective:2000px]"
                        key="right-pane-top"
                    >
                        {status === 'canceled' ? (
                            <div className="mx-auto h-full flex items-center gap-1 text-muted-foreground">
                                <StatusIcon status={'canceled'} />
                                <p>The run was canceled</p>
                            </div>
                        ) : status === 'finished' ? (
                            videoComponent
                        ) : status === 'queued' || sessionStatus !== 'sessionReady' ? (
                            <div className="mx-auto h-full flex items-center gap-1 text-muted-foreground">
                                <StatusIcon status={'queued'} />
                                <p>The run is starting</p>
                            </div>
                        ) : status === 'running-testagent' ? (
                            <div className="mx-auto h-full flex items-center gap-1 text-muted-foreground">
                                <StatusIcon status={'queued'} />
                                <p>The agent is now analyzing test results and processing the replay video</p>
                            </div>
                        ) : (
                            status === 'running' &&
                            sessionStatus === 'sessionReady' &&
                            debugPageId && (
                                <>
                                    <div
                                        className="flex flex-row marker:items-center justify-between
                                    rounded-sm p-2 whitespace-nowrap min-w-0 max-w-full bg-slate-100 text-slate-500"
                                    >
                                        <p className="text-small truncate [text-box:trim-both]">
                                            {currentUrl ?? 'loading url ...'}
                                        </p>
                                    </div>
                                    <div>
                                        <div className="vp-container">
                                            <iframe
                                                src={`https://screencast-chi.vercel.app/inspector.html?${wsProtocol}=${wsUrl}`}
                                                className="w-full h-full border-0 pointer-events-none overflow-hidden"
                                                style={{ aspectRatio: 1280 / 720, maxHeight: 'calc(100% - 28px)' }}
                                                title="Inspector"
                                                allowFullScreen
                                            />
                                        </div>
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </div>
            </Split>
        </div>
    );
});

// Use to wrap the RunViewer component with the IoProvider and
// reset state on navigate to same component with the key
const RunViewerWithIoProvider = () => {
    const { runId } = useParams();

    return (
        <IoProvider>
            <RunViewer key={runId} />
        </IoProvider>
    );
};

export default RunViewerWithIoProvider;
